import { PolicyLinks } from "./ApplicationPolicy";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { Button } from "../components/ui/Button";
import { NonWorkspacePage } from "../components/layout/NonWorkspacePage";
import { OperatorSurface } from "../components/ui/OperatorSurface";

const linkClass =
  "flex items-center justify-between rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 shadow-sm hover:border-slate-300";

export function Settings() {
  const { signOut, loading } = useAuth();
  const { activeBrand, isLoading } = useActiveBrand();

  return (
    <NonWorkspacePage width="narrow" header={{
        eyebrow: "Account",
        title: "Account settings",
        description: "Manage account-level controls for connections, billing, usage, session access, and portable CLI setup.",
        breadcrumbs: [
          { label: "Workspace", href: "/home" },
          { label: "Account settings" },
        ]
    }}>

      <PolicyLinks />
      <nav className="flex flex-col gap-2" aria-label="Account sections">
        <Link to="/brands" className={linkClass}>
          <span>Brands</span>
          <span className="text-slate-400">→</span>
        </Link>
        <Link to="/settings/connections" className={linkClass}>
          <span>Connections</span>
          <span className="text-slate-400">→</span>
        </Link>
        <Link to="/settings/tokens" className={linkClass}>
          <span>API tokens</span>
          <span className="text-slate-400">→</span>
        </Link>
        <Link to="/usage" className={linkClass}>
          <span>Billing &amp; Usage</span>
          <span className="text-slate-400">→</span>
        </Link>
      </nav>

      <OperatorSurface className="bg-slate-50 text-sm text-slate-700">
        <h2 className="text-sm font-semibold text-slate-950">Brand-scoped controls stay in the workspace</h2>
        <p className="mt-2">
          Brand docs, agent chat, tasks, workflows, and brand utilities remain tied to the active
          brand in the left rail. Authentication lives here at the account level, then gets assigned
          back into those brand workflows.
        </p>
        {activeBrand ? (
          <p className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-800">
            Active brand: <span className="font-semibold">{activeBrand.name}</span>. Use the left
            rail to jump back into brand work or switch brands.
          </p>
        ) : null}
        {!isLoading && !activeBrand ? (
          <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
            No brand selected. Return to <Link className="font-medium underline" to="/home">Workspace</Link> to pick a brand.
          </p>
        ) : null}
      </OperatorSurface>

      <OperatorSurface className="border-dashed text-sm text-slate-700">
        <h2 className="text-sm font-semibold text-slate-950">Portable CLI access</h2>
        <p className="mt-2">
          Create and revoke CLI tokens from a dedicated account route so external consumer access
          stays separate from brand-owned settings.
        </p>
        <div className="mt-4">
          <Link
            to="/settings/tokens"
            className="inline-flex rounded-xl border border-slate-300 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Manage API tokens
          </Link>
        </div>
      </OperatorSurface>

      <div className="border-t border-slate-200 pt-6">
        <Button
          type="button"
          variant="secondary"
          className="w-full"
          disabled={loading}
          onClick={() => void signOut()}
        >
          {loading ? "Signing out…" : "Sign out"}
        </Button>
      </div>
    </NonWorkspacePage>
  );
}
